import { CampaignPOAllocation } from '../../useCampaignAllocations';
import styles from './CampaignAllocations.module.css';
import cn from 'classnames';
import { Button, assets } from '@round/whitelabel-ui';
import { useUpdatePurchaseOrderLine } from '@round/creatorbase-helpers/api-hooks';
import AllocationInput from '../AllocationInput/AllocationInput';
import { useState } from 'react';
import { creatorbase } from '@round/api';
import { asMoney, showNotification } from 'helpers';
import { useCurrencies } from 'contexts/OptionsContext/useCurrencies';
import { VirtualList } from '@round/whitelabel-ui';
import { Skeleton } from '@round/ui-kit';
import range from 'lodash/range';

const { Trash: TrashIcon } = assets;

type Props = {
    allocations: CampaignPOAllocation[];
    isLoading: boolean;
    error: string | null;
    hasNextPage: boolean;
    fetchNextPage: () => void;
} & Pick<AllocationRowProps, 'campaign' | 'onDelete' | 'onUpdated'>;

const TITLE = 'Current Allocations';

const CampaignAllocations = ({
    allocations,
    error,
    isLoading,
    campaign,
    onDelete,
    onUpdated,
    hasNextPage,
    fetchNextPage,
}: Props) => {
    if (error) {
        return (
            <div className={styles.container}>
                <p className={styles.title}>{TITLE}</p>
                <p className={cn(styles.error, styles.row)}>{error}</p>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <p className={styles.title}>{TITLE}</p>

            {isLoading && !allocations.length ? (
                range(5).map(() => (
                    <div className={styles.row}>
                        <Skeleton className={styles.skeleton} containerClassName={styles.skeletonContainer} />
                    </div>
                ))
            ) : allocations.length ? (
                <VirtualList
                    className={styles.scrollView}
                    data={allocations}
                    options={{ count: allocations.length, estimateSize: () => 36, gap: 8 }}
                    onEndReached={() => {
                        if (hasNextPage && !isLoading) {
                            fetchNextPage();
                        }
                    }}
                >
                    {(allocation) => (
                        <AllocationRow
                            key={`${allocation.id}${allocation.amount}`}
                            allocation={allocation}
                            campaign={campaign}
                            onDelete={onDelete}
                            onUpdated={onUpdated}
                            isLoading={isLoading}
                        />
                    )}
                </VirtualList>
            ) : (
                <p className={styles.empty}>No allocations yet</p>
            )}
        </div>
    );
};

type AllocationRowProps = {
    isReadonly?: boolean;
    allocation: CampaignPOAllocation;
    campaign: creatorbase.Campaign | null | undefined;
    onDelete: (allocation: CampaignPOAllocation) => void;
    onUpdated: (updatedAllocation: CampaignPOAllocation) => void;
    isLoading: boolean;
};

const AllocationRow = ({ isReadonly, allocation, campaign, onDelete, onUpdated, isLoading }: AllocationRowProps) => {
    const { getCurrencyById } = useCurrencies();
    const [amount, setAmount] = useState<string | undefined>(allocation.amount.toString());
    const { update, isLoading: isUpdateLoading, helpers } = useUpdatePurchaseOrderLine(allocation.id);

    const campaignUnallocated = (campaign?.cost ?? 0) - (campaign?.purchase_orders_allocated_amount ?? 0);
    const max = Math.min(
        allocation.amount + campaignUnallocated,
        (allocation.purchaseOrder?.remaining_amount ?? 0) + allocation.amount
    );

    const isAmountLargerThanMaxValue = parseFloat(amount ?? '0') > max;

    const submit = () => {
        const parsedAmount = parseFloat(amount || '');
        if (!isFinite(parsedAmount)) {
            showNotification('Must be a number', 'error');
            return;
        }

        if (parsedAmount === allocation.amount) {
            return;
        }

        update(
            { id: allocation.id, amount: parsedAmount },
            {
                onSuccess: (data) => {
                    onUpdated({ ...allocation, ...data });
                    showNotification('Updated', 'info');
                },
                onError: (error) => {
                    showNotification(helpers.getErrorMessage(error), 'error');
                },
            }
        );
    };

    if (isLoading) {
        return (
            <div className={styles.row}>
                <Skeleton className={styles.skeleton} containerClassName={styles.skeletonContainer} />
            </div>
        );
    }

    return (
        <>
            <p className={styles.row}>
                <span className={styles.poNumber}>
                    {allocation.purchaseOrder?.number ?? allocation.purchaseOrder?.request_reference}
                </span>

                <div className={styles.actions}>
                    <AllocationInput
                        readOnly={isReadonly}
                        className={styles.allocationInput}
                        disabled={isUpdateLoading}
                        max={max}
                        value={amount}
                        onValueChange={setAmount}
                        currencyId={allocation.purchaseOrder?.currency_id}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                submit();
                            }
                        }}
                        onBlur={submit}
                    />

                    {!isReadonly && (
                        <Button
                            className={cn(styles.button, styles.deleteButton)}
                            appearance="ghost"
                            type="button"
                            onClick={() => onDelete(allocation)}
                        >
                            <TrashIcon />
                        </Button>
                    )}
                </div>
            </p>

            {isAmountLargerThanMaxValue && (
                <p className={styles.maxAmountWarning}>Max: {asMoney(max, getCurrencyById(campaign?.currency_id))}</p>
            )}
        </>
    );
};

export default CampaignAllocations;
